import React, { useState } from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"
import { normalizeLegacyPresetValue } from "../utils/presetVocabulary"

interface PauseAnalysisConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

type PausePresetKey = "strict" | "normal" | "lenient" | "custom"

const DEFAULT_CUSTOM_MAD_Z_SCORE_THRESHOLD = 3.0

const PRESET_OPTIONS: Array<{
  key: PausePresetKey
  label: string
  description: string
}> = [
  {
    key: "strict",
    label: "Strict",
    description: "Flags more pauses as anomalies (z=2.0)",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Balanced sensitivity (z=3.0)",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags large outliers (z=4.0)",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own threshold",
  },
]

export const PauseAnalysisConfig: React.FC<PauseAnalysisConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const storedPreset = normalizeLegacyPresetValue(
    metricRecordData.metric_metadata?.pause_detection_preset,
  )
  const hasThresholdAtMount =
    metricRecordData.metric_metadata?.mad_z_score_threshold !== undefined &&
    metricRecordData.metric_metadata?.mad_z_score_threshold !== null
  const [currentPreset, setCurrentPreset] = useState<PausePresetKey>(
    hasThresholdAtMount
      ? "custom"
      : ((storedPreset as PausePresetKey | undefined) ?? "normal"),
  )
  const isCustom = currentPreset === "custom"

  const handlePresetChange = (value: string) => {
    const selectedKey = value as PausePresetKey
    setCurrentPreset(selectedKey)

    const nextMetadata = {
      ...metricRecordData.metric_metadata,
      pause_detection_preset:
        selectedKey === "custom" ? undefined : selectedKey,
    }
    if (selectedKey === "custom") {
      nextMetadata.mad_z_score_threshold =
        metricRecordData.metric_metadata?.mad_z_score_threshold ??
        DEFAULT_CUSTOM_MAD_Z_SCORE_THRESHOLD
    } else {
      nextMetadata.mad_z_score_threshold = undefined
    }

    onChange({
      ...metricRecordData,
      metric_metadata: nextMetadata,
    })
  }

  const handleMinPauseDurationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_pause_duration_seconds: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMadZScoreThresholdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        mad_z_score_threshold: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMadZScoreThresholdBlur = () => {
    if (!isCustom) {
      return
    }
    const current = metricRecordData.metric_metadata?.mad_z_score_threshold
    if (current == null) {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          mad_z_score_threshold: DEFAULT_CUSTOM_MAD_Z_SCORE_THRESHOLD,
        },
      })
    }
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Minimum Pause Duration (seconds)
          </label>
        </div>
        <input
          type="number"
          step="0.1"
          min="0"
          className={TEXT_INPUT_STYLES}
          value={
            metricRecordData.metric_metadata?.min_pause_duration_seconds ?? ""
          }
          onChange={handleMinPauseDurationChange}
          placeholder="1.0"
          aria-label="Minimum Pause Duration"
        />
        <p className="text-xs text-coval-text-muted">
          Pauses shorter than this duration will be ignored in the analysis.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Anomaly Detection Preset</span>
        </div>
        <Select value={currentPreset} onValueChange={handlePresetChange}>
          <SelectTrigger aria-label="Anomaly Detection Preset">
            <SelectValue placeholder="Select preset" />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Controls how sensitive anomalous pause detection is. Choose Custom to
          set your own threshold.
        </p>
      </div>

      {isCustom && (
        <div className={FIELD_CONTAINER_STYLES}>
          <div className={`${LABEL_ROW_STYLES} min-h-10`}>
            <span className={LABEL_STYLES}>MAD z-score Threshold</span>
            <Tooltip
              content="Pauses with a MAD-based modified z-score at or above this value are flagged as anomalous. Lower values flag more pauses."
              side="top"
            >
              <button type="button" className="inline-flex" aria-label="Info">
                <Info className={INFO_ICON_STYLES} />
              </button>
            </Tooltip>
          </div>
          <input
            type="number"
            step="0.1"
            min="0"
            className={TEXT_INPUT_STYLES}
            value={
              metricRecordData.metric_metadata?.mad_z_score_threshold ?? ""
            }
            onChange={handleMadZScoreThresholdChange}
            onBlur={handleMadZScoreThresholdBlur}
            placeholder="3.0"
            aria-label="MAD z-score Threshold"
          />
        </div>
      )}
    </div>
  )
}
